/*
 * <cart-drawer> (cart workstream, w25 cart A): a drawer on desktop, full screen on phones.
 * Keeps Dawn's element API (open, close, renderContents, getSectionsToRender, setActiveElement)
 * so native product forms keep working; all cart logic lives in asc-cart-api.js (window.ascCart).
 * Loaded by snippets/cart-drawer.liquid and (legacy) by the header, so the definition is guarded.
 */
(function () {
  if (customElements.get('cart-drawer')) return;

  // Last input type: the heading gets focus when the drawer opens (for screen readers and Tab order). Its focus ring is
  // only shown when the drawer was opened from the keyboard; after a tap, a click or a script it stays invisible.
  var lastInputKeyboard = false;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key.indexOf('Arrow') === 0) lastInputKeyboard = true;
  }, true);
  ['pointerdown', 'mousedown', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, function () { lastInputKeyboard = false; }, { capture: true, passive: true });
  });

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.panel = this.querySelector('.ascc-panel');
      this.onKey = this.onKey.bind(this);
      var sentinel = this.querySelector('[data-ascc-sentinel]');
      if (sentinel) sentinel.addEventListener('focus', this.onSentinel.bind(this));
      this.bindHeaderIcon();
    }

    // The header cart icon is handled by delegation in asc-cart-api.js (survives header re-renders).
    bindHeaderIcon() {
      var link = document.getElementById('cart-icon-bubble');
      var cfg = window.ascCartConfig || {};
      if (!link || cfg.isCartPage) return;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    }

    setActiveElement(el) {
      this.activeElement = el;
    }

    isOpen() {
      return this.classList.contains('active');
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      else if (!this.isOpen()) this.setActiveElement(document.activeElement);
      if (this.isOpen()) return;
      var self = this;
      this.classList.toggle('ascc-keyboard-open', lastInputKeyboard);
      this.classList.add('is-mounted');
      // next frame so the slide-in transition runs
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self.classList.add('animate', 'active');
        });
      });
      document.documentElement.classList.add('ascc-lock');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKey, true);
      setTimeout(function () {
        var title = self.querySelector('.ascc-title');
        (title || self.panel).focus({ preventScroll: true });
      }, 60);
      document.dispatchEvent(new CustomEvent('asc:cart:open', { detail: { __fromApi: true } }));
    }

    close() {
      if (!this.isOpen()) return;
      var self = this;
      this.classList.remove('active');
      document.documentElement.classList.remove('ascc-lock');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKey, true);
      setTimeout(function () {
        if (!self.isOpen()) self.classList.remove('is-mounted', 'animate');
      }, 320);
      var back = this.activeElement;
      if (back && document.contains(back) && typeof back.focus === 'function') back.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('asc:cart:close'));
    }

    onKey(e) {
      if (!this.isOpen()) return;
      if (e.key === 'Escape') {
        // let an open "Change model" panel close first
        var openPanel = this.querySelector('[data-asc-change-model][aria-expanded="true"]');
        if (openPanel && openPanel.closest('.ascc-card') && openPanel.closest('.ascc-card').contains(document.activeElement)) {
          e.preventDefault();
          openPanel.click();
          openPanel.focus();
          return;
        }
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      // visually hidden radios are focusable but have no offsetParent in some engines
      var list = Array.prototype.slice.call(this.panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        if (el.closest('[hidden]') || el.hasAttribute('data-ascc-sentinel')) return false;
        var s = window.getComputedStyle(el);
        return s.visibility !== 'hidden' && s.display !== 'none';
      });
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var a = document.activeElement;
      if (!this.panel.contains(a)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (a === first || a === this.panel || a.classList.contains('ascc-title'))) {
        e.preventDefault();
        last.focus();
      }
      // Forward: no wrap here. Tab runs on from Checkout into the express buttons (shadow DOM / iframes that
      // querySelectorAll can't see) and lands on the end sentinel, which sends focus back to the top.
    }

    onSentinel() {
      var title = this.querySelector('.ascc-x') || this.querySelector('.ascc-title');
      if (title) title.focus();
    }

    // Dawn product-form.js compatibility: it posts to /cart/add itself, then hands us the response.
    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#AscCartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    renderContents() {
      var self = this;
      this.classList.remove('is-empty');
      var go = function () {
        window.ascCart.refresh().then(function () {
          self.open();
        });
      };
      if (window.ascCart) go();
      else document.addEventListener('asc:cart:ready', go, { once: true });
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
